import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowUp, ArrowDown, ArrowRight, ArrowLeft as ArrowL, Home, RotateCcw, Trophy, Medal, Gamepad2, HelpCircle, Square } from "lucide-react";
import api, { apiErr } from "../../lib/api";
import { ThemedShell, ThemedCard } from "../../components/child/ThemedShell";
import { Button } from "../../components/ui/button";
import { sfx, music } from "../../lib/sound";
import { toast } from "sonner";

const COLS = 13, ROWS = 16, MAX_LEVEL = 10, START_LIVES = 2;
const COLORS = [
  { name: "Rood", c: "#e0405b" }, { name: "Blauw", c: "#2f7ff0" }, { name: "Groen", c: "#2bb673" },
  { name: "Geel", c: "#f4c40f" }, { name: "Paars", c: "#9b5de5" }, { name: "Oranje", c: "#f5871f" },
];
const DIRS = { up: [-1, 0], down: [1, 0], left: [0, -1], right: [0, 1] };

function mulberry(seed) { return function () { seed |= 0; seed = (seed + 0x6D2B79F5) | 0; let t = Math.imul(seed ^ (seed >>> 15), 1 | seed); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; }; }

export default function Digger() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState("menu"); // menu|play|over|win|board
  const [color, setColor] = useState(COLORS[0].c);
  const [showPad, setShowPad] = useState(true);
  const [hud, setHud] = useState({ level: 1, lives: START_LIVES, score: 0, emer: 0 });
  const [result, setResult] = useState(null);
  const [board, setBoard] = useState(null);
  const [banner, setBanner] = useState(null);
  const [showHelp, setShowHelp] = useState(() => localStorage.getItem("la_digger_help") !== "seen");
  const closeHelp = () => { setShowHelp(false); localStorage.setItem("la_digger_help", "seen"); };

  const canvasRef = useRef(null);
  const dim = useRef({ W: 400, H: 600, cell: 30, ox: 0, oy: 0 });
  const G = useRef(null);
  const rafRef = useRef(0);
  const phaseRef = useRef(phase); phaseRef.current = phase;
  const colorRef = useRef(color); colorRef.current = color;
  const scoreRef = useRef(0);

  const size = () => {
    const cv = canvasRef.current; if (!cv) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const W = cv.clientWidth, H = cv.clientHeight;
    cv.width = Math.round(W * dpr); cv.height = Math.round(H * dpr);
    const cell = Math.floor(Math.min(W / COLS, H / ROWS));
    dim.current = { W, H, cell, dpr, ox: Math.floor((W - cell * COLS) / 2), oy: Math.floor((H - cell * ROWS) / 2) };
  };

  const buildLevel = (level, lives, score) => {
    const rnd = mulberry(1000 + level * 7);
    const dug = Array.from({ length: ROWS }, () => Array(COLS).fill(false));
    const em = Array.from({ length: ROWS }, () => Array(COLS).fill(false));
    const bags = [];

    // --- tunnelnetwerk graven: aliens lopen ALLEEN over gegraven cellen ---
    const carveRow = (r, c0, c1) => { for (let c = Math.max(0, c0); c <= Math.min(COLS - 1, c1); c++) if (r >= 0 && r < ROWS) dug[r][c] = true; };
    const carveCol = (c, r0, r1) => { for (let r = Math.max(0, r0); r <= Math.min(ROWS - 1, r1); r++) if (c >= 0 && c < COLS) dug[r][c] = true; };
    const pr = 1, pc = COLS - 2;          // speler start (rechtsboven)
    const nest = { r: ROWS - 2, c: 1 };   // alien-nest (linksonder)
    carveRow(1, 0, COLS - 1);             // bovengang
    carveCol(1, 1, ROWS - 2);             // linkergang naar nest
    const midRows = [4 + Math.floor(rnd() * 2), 8 + Math.floor(rnd() * 2), 12 + Math.floor(rnd() * 2)];
    for (const mr of midRows) carveRow(mr, 1, 2 + Math.floor(rnd() * (COLS - 4)));
    const vc = 4 + Math.floor(rnd() * (COLS - 6));
    carveCol(vc, 1, ROWS - 2);            // verticale verbinding

    // --- smaragden alleen in vaste aarde (niet in gangen) ---
    let emer = 0;
    const blocks = 2 + Math.min(3, Math.floor(level / 2));
    for (let b = 0; b < blocks; b++) {
      const r0 = 2 + Math.floor(rnd() * (ROWS - 6));
      const c0 = 1 + Math.floor(rnd() * (COLS - 4));
      const w = 2 + Math.floor(rnd() * 2), h = 2 + Math.floor(rnd() * 3);
      for (let r = r0; r < Math.min(ROWS - 1, r0 + h); r++)
        for (let c = c0; c < Math.min(COLS - 1, c0 + w); c++)
          if (!dug[r][c] && !em[r][c]) { em[r][c] = true; emer++; }
    }
    if (emer === 0) { em[ROWS - 4][COLS - 3] = true; emer = 1; }

    // --- goudzakken in vaste aarde (te duwen / te laten vallen) ---
    const nbags = 3 + level;
    for (let i = 0; i < nbags; i++) {
      const r = 1 + Math.floor(rnd() * (ROWS - 3)), c = Math.floor(rnd() * COLS);
      if (!dug[r][c] && !em[r][c] && (r + 1 >= ROWS || !dug[r + 1][c]) && !bags.some(b => b.r === r && b.c === c)) bags.push({ r, c, state: "rest", wob: 0, y: 0, fell: 0 });
    }

    // --- kers op een gegraven cel (onder de verticale gang, weg van nest) ---
    const cherry = { r: ROWS - 2, c: vc };
    // --- aliens in het nest, op de gegraven linkergang ---
    const enemies = [];
    const ne = 2 + Math.floor(level / 2);
    const grace = level <= 2 ? 5 : 3; // aanloop-vertraging: kinderen krijgen tijd om te starten
    for (let i = 0; i < ne; i++) enemies.push({ r: Math.max(1, nest.r - i), c: 1, x: 0, y: 0, dir: "up", spawnT: grace + i * 2.0, alive: true });

    const cell = dim.current.cell;
    const px = { r: pr, c: pc, dir: "left", nextDir: "left", stopped: true, moving: false, tx: 0, ty: 0 };
    return {
      dug, em, bags, enemies, cherry, hasCherry: true, scared: 0, level, lives, score, emer,
      player: px, speed: cell * (4.4 + level * 0.2), espeed: cell * (2.6 + level * 0.22),
      t: 0, dead: false, paused: 0, pops: [],
    };
  };

  const cellCenter = (r, c) => { const { ox, oy, cell } = dim.current; return { x: ox + c * cell + cell / 2, y: oy + r * cell + cell / 2 }; };
  const aligned = (px) => { const ct = cellCenter(px.r, px.c); return Math.abs(px.x - ct.x) < 1 && Math.abs(px.y - ct.y) < 1; };

  const passable = (g, r, c) => r >= 0 && r < ROWS && c >= 0 && c < COLS && !g.bags.some(b => b.r === r && b.c === c && (b.state === "rest" || b.state === "wobble" || b.state === "fall"));

  const startGame = () => { G.current = null; setHud({ level: 1, lives: START_LIVES, score: 0, emer: 0 }); scoreRef.current = 0; setResult(null); setBanner(null); setPhase("play"); };

  // input
  const setDir = (d) => { const g = G.current; if (g && !g.dead) { g.player.nextDir = d; g.player.stopped = false; sfx.click && sfx.click(); } };
  const stopDigger = () => { const g = G.current; if (g && !g.dead) g.player.stopped = true; };
  const touch = useRef({ x: 0, y: 0 });
  const onDown = (e) => { touch.current = { x: e.clientX, y: e.clientY }; };
  const onUp = (e) => {
    const dx = e.clientX - touch.current.x, dy = e.clientY - touch.current.y;
    if (Math.abs(dx) < 16 && Math.abs(dy) < 16) { stopDigger(); return; } // tik = stilstaan
    if (Math.abs(dx) > Math.abs(dy)) setDir(dx > 0 ? "right" : "left"); else setDir(dy > 0 ? "down" : "up");
  };
  useEffect(() => {
    const onKey = (e) => { const m = { ArrowUp: "up", ArrowDown: "down", ArrowLeft: "left", ArrowRight: "right" }[e.key]; if (m) { e.preventDefault(); setDir(m); } };
    window.addEventListener("keydown", onKey); return () => window.removeEventListener("keydown", onKey);
  }, []);

  // dev-only testhook (in productie weg-getreeshaked)
  useEffect(() => {
    if (process.env.NODE_ENV === "production") return;
    window.__diggerTest = {
      clear: () => { const g = G.current; if (g) levelClear(g); },
      defeat: () => { const g = G.current; if (!g) return; const e = g.enemies.find(x => x.alive); if (!e) return; e.alive = false; g.score += 200; scoreRef.current = g.score; const ct = cellCenter(e.r, e.c); g.pops.push({ x: e.x || ct.x, y: e.y || ct.y, text: "+200", t: 0 }); },
    };
    return () => { delete window.__diggerTest; };
  }, []);

  // ---- loop ----
  useEffect(() => {
    if (phase !== "play") return;
    size();
    if (!G.current) G.current = buildLevel(1, START_LIVES, 0);
    music.start();
    let last = 0;
    const loop = (ts) => {
      if (!last) last = ts; let dt = (ts - last) / 1000; last = ts; if (dt > 0.05) dt = 0.05;
      const g = G.current;
      if (g && !g.dead && g.paused <= 0) update(g, dt); else if (g && g.paused > 0) g.paused -= dt;
      render();
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(rafRef.current); music.stop(); };
  }, [phase]);

  const moveEntity = (g, e, speed, dt, isPlayer) => {
    const ct = cellCenter(e.r, e.c);
    const atCenter = Math.abs(e.x - ct.x) < 1 && Math.abs(e.y - ct.y) < 1;
    // wendbaarheid: directe U-bocht midden in een gang
    if (isPlayer && !e.stopped && !atCenter) {
      const REV = { up: "down", down: "up", left: "right", right: "left" };
      if (e.nextDir === REV[e.dir]) { e.dir = e.nextDir; e.tr = e.r; e.tc = e.c; }
    }
    if (atCenter) {
      e.x = ct.x; e.y = ct.y;
      if (isPlayer) {
        if (e.stopped) { e.tr = e.r; e.tc = e.c; return; } // stilstaan
        const tryDir = (dir) => {
          const [dr, dc] = DIRS[dir] || [0, 0];
          const nr = e.r + dr, nc = e.c + dc;
          if (passable(g, nr, nc)) return { nr, nc };
          // duw een rustende goudzak zijwaarts in een open gang
          if (dir === "left" || dir === "right") {
            const bag = g.bags.find(b => b.r === nr && b.c === nc && b.state === "rest");
            if (bag) {
              const bnc = nc + dc;
              if (bnc >= 0 && bnc < COLS && g.dug[nr][bnc] && !g.bags.some(o => o !== bag && o.r === nr && o.c === bnc)) {
                // verpletter een alien op de bestemmingscel (zak schuift hem omver)
                g.enemies.forEach(en => {
                  if (en.alive && en.r === nr && en.c === bnc) {
                    en.alive = false; g.score += 200; scoreRef.current = g.score;
                    const cc = cellCenter(nr, bnc); g.pops.push({ x: cc.x, y: cc.y, text: "+200", t: 0 });
                    sfx.coin && sfx.coin();
                  }
                });
                bag.c = bnc; sfx.click && sfx.click(); return { nr, nc };
              }
            }
          }
          return null;
        };
        let res = tryDir(e.nextDir);
        if (res) e.dir = e.nextDir; else res = tryDir(e.dir);
        if (!res) { e.stopped = true; return; }
        e.tr = res.nr; e.tc = res.nc;
      } else {
        const [dr, dc] = DIRS[e.dir] || [0, 0];
        const nr = e.r + dr, nc = e.c + dc;
        if (!(passable(g, nr, nc) && nr >= 0 && nr < ROWS && nc >= 0 && nc < COLS && g.dug[nr][nc])) return false;
        e.tr = nr; e.tc = nc;
      }
    }
    const tc2 = cellCenter(e.tr ?? e.r, e.tc ?? e.c);
    const sx = Math.sign(tc2.x - e.x), sy = Math.sign(tc2.y - e.y);
    e.x += sx * speed * dt; e.y += sy * speed * dt;
    if ((sx !== 0 && Math.sign(tc2.x - e.x) !== sx) || (sy !== 0 && Math.sign(tc2.y - e.y) !== sy)) { e.x = tc2.x; e.y = tc2.y; }
    if (Math.abs(e.x - tc2.x) < 1 && Math.abs(e.y - tc2.y) < 1) { e.x = tc2.x; e.y = tc2.y; e.r = e.tr ?? e.r; e.c = e.tc ?? e.c; if (isPlayer) onEnterCell(g, e.r, e.c); }
    return true;
  };

  const onEnterCell = (g, r, c) => {
    if (g.em[r][c]) { g.em[r][c] = false; g.emer--; g.score += 25; scoreRef.current = g.score; sfx.coin && sfx.coin(); if (g.emer <= 0) levelClear(g); }
    g.dug[r][c] = true;
    const bi = g.bags.findIndex(b => b.r === r && b.c === c && b.state === "gold");
    if (bi >= 0) { g.bags.splice(bi, 1); g.score += 250; scoreRef.current = g.score; sfx.win && sfx.win(); }
    if (g.hasCherry && g.cherry.r === r && g.cherry.c === c) { g.hasCherry = false; g.scared = 7; sfx.correct && sfx.correct(); }
  };

  const enemyThink = (g, e) => {
    const opts = [];
    for (const d in DIRS) { const [dr, dc] = DIRS[d]; const nr = e.r + dr, nc = e.c + dc; if (nr >= 0 && nr < ROWS && nc >= 0 && nc < COLS && g.dug[nr][nc] && passable(g, nr, nc)) opts.push({ d, nr, nc }); }
    if (!opts.length) return;
    const pr = g.player.r, pc = g.player.c;
    opts.sort((a, b) => { const da = Math.abs(a.nr - pr) + Math.abs(a.nc - pc), db = Math.abs(b.nr - pr) + Math.abs(b.nc - pc); return g.scared > 0 ? db - da : da - db; });
    const rev = { up: "down", down: "up", left: "right", right: "left" }[e.dir];
    const best = opts.find(o => o.d !== rev) || opts[0];
    e.dir = best.d;
  };

  const update = (g, dt) => {
    g.t += dt; if (g.scared > 0) g.scared -= dt;
    moveEntity(g, g.player, g.speed, dt, true);
    // bags
    const { oy, cell } = dim.current;
    for (const b of g.bags) {
      if (b.state === "gold") continue;
      const below = b.r + 1;
      const emptyBelow = below < ROWS && g.dug[below][b.c] && !g.bags.some(o => o !== b && o.r === below && o.c === b.c && o.state !== "gold");
      if (b.state === "rest") { if (emptyBelow) { b.state = "wobble"; b.wob = 0.45; } }
      else if (b.state === "wobble") { b.wob -= dt; if (!emptyBelow) { b.state = "rest"; } else if (b.wob <= 0) { b.state = "fall"; b.fell = 0; } }
      else if (b.state === "fall") {
        b.y += g.speed * 1.1 * dt;
        if (b.y >= cell) { b.y = 0; b.r++; b.fell++;
          // crush
          if (g.player.r === b.r && g.player.c === b.c) die(g);
          g.enemies.forEach(e => {
            if (e.alive && e.r === b.r && e.c === b.c) {
              e.alive = false; g.score += 200; scoreRef.current = g.score;
              const ct = cellCenter(b.r, b.c); g.pops.push({ x: ct.x, y: ct.y, text: "+200", t: 0 });
              sfx.coin && sfx.coin();
            }
          });
          const stop = (b.r + 1 >= ROWS) || !g.dug[b.r + 1][b.c] || g.bags.some(o => o !== b && o.r === b.r + 1 && o.c === b.c);
          if (stop) { b.state = b.fell >= 1 ? "gold" : "rest"; b.y = 0; }
        }
      }
    }
    // enemies
    for (const e of g.enemies) {
      if (!e.alive) continue;
      if (g.t < e.spawnT) continue;
      if (e.x === 0 && e.y === 0) { const ct = cellCenter(e.r, e.c); e.x = ct.x; e.y = ct.y; }
      const ct = cellCenter(e.r, e.c);
      if (Math.abs(e.x - ct.x) < 1 && Math.abs(e.y - ct.y) < 1) enemyThink(g, e);
      moveEntity(g, e, g.espeed * (g.scared > 0 ? 0.6 : 1), dt, false);
      // contact player
      if (Math.abs(e.x - g.player.x) < cell * 0.6 && Math.abs(e.y - g.player.y) < cell * 0.6) {
        if (g.scared > 0) { e.alive = false; g.score += 200; scoreRef.current = g.score; g.pops.push({ x: e.x, y: e.y, text: "+200", t: 0 }); sfx.coin && sfx.coin(); }
        else die(g);
      }
    }
    // drijvende score-popups
    if (g.pops.length) { for (const p of g.pops) p.t += dt; g.pops = g.pops.filter(p => p.t < 1.0); }
    setHud({ level: g.level, lives: g.lives, score: g.score, emer: g.emer });
  };

  const die = (g) => {
    if (g.dead) return; g.dead = true; sfx.wrong && sfx.wrong(); g.lives--;
    setTimeout(() => {
      if (g.lives < 0) { endGame(g, false); return; }
      // respawn
      g.player.r = 1; g.player.c = COLS - 2; const ct = cellCenter(1, COLS - 2); g.player.x = ct.x; g.player.y = ct.y; g.player.dir = "left"; g.player.nextDir = "left"; g.player.stopped = true; g.dug[1][COLS - 2] = true;
      for (const e of g.enemies) { e.x = 0; e.y = 0; e.spawnT = g.t + 1.5; }
      g.scared = 0; g.dead = false; g.paused = 0.4;
    }, 700);
  };

  const levelClear = (g) => {
    const bonus = 500; g.score += bonus; scoreRef.current = g.score; g.dead = true;
    sfx.win && sfx.win();
    if (g.level >= MAX_LEVEL) { setTimeout(() => endGame(g, true), 500); return; }
    setBanner({ level: g.level, bonus, score: g.score });
    setTimeout(() => {
      const ng = buildLevel(g.level + 1, g.lives, g.score);
      const ct = cellCenter(ng.player.r, ng.player.c); ng.player.x = ct.x; ng.player.y = ct.y;
      G.current = ng;
      setBanner(null);
    }, 1600);
  };

  const endGame = async (g, won) => {
    cancelAnimationFrame(rafRef.current);
    setPhase(won ? "win" : "over");
    try { const { data } = await api.post("/child/digger/score", { score: g.score, level: g.level }); setResult({ ...data, score: g.score, level: g.level, won }); }
    catch (e) { setResult({ score: g.score, level: g.level, won }); }
  };

  // ---- render ----
  const render = () => {
    const cv = canvasRef.current; if (!cv) return; const ctx = cv.getContext("2d");
    const { W, H, dpr, cell, ox, oy } = dim.current; const g = G.current;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.fillStyle = "#0a0a0a"; ctx.fillRect(0, 0, W, H);
    if (!g) return;
    for (let r = 0; r < ROWS; r++) for (let c = 0; c < COLS; c++) {
      const x = ox + c * cell, y = oy + r * cell;
      if (g.dug[r][c]) {
        // uitgegraven gang: donkere grond met diepte
        ctx.fillStyle = "#160f09"; ctx.fillRect(x, y, cell, cell);
        ctx.fillStyle = "rgba(120,80,40,0.10)"; ctx.fillRect(x, y, cell, cell * 0.16);
        ctx.fillStyle = "rgba(0,0,0,0.40)"; ctx.fillRect(x, y + cell * 0.84, cell, cell * 0.16);
      } else {
        // aarde met 3D-bevel + lagen + kiezels
        ctx.fillStyle = "#b5541d"; ctx.fillRect(x, y, cell, cell);
        const bv = Math.max(2, cell * 0.12);
        ctx.fillStyle = "rgba(255,200,120,0.22)"; ctx.fillRect(x, y, cell, bv);
        ctx.fillStyle = "rgba(0,0,0,0.22)"; ctx.fillRect(x, y + cell - bv, cell, bv);
        ctx.fillStyle = "rgba(0,0,0,0.10)"; ctx.fillRect(x + cell - Math.max(2, cell * 0.10), y, Math.max(2, cell * 0.10), cell);
        ctx.strokeStyle = "rgba(90,40,10,0.28)"; ctx.lineWidth = 1; ctx.beginPath();
        ctx.moveTo(x, y + cell * 0.5); ctx.lineTo(x + cell, y + cell * 0.5); ctx.stroke();
        const h = (r * 31 + c * 17) % 7;
        ctx.fillStyle = "rgba(255,210,150,0.18)"; ctx.beginPath(); ctx.arc(x + cell * (0.25 + (h % 3) * 0.22), y + cell * (0.3 + (h % 2) * 0.3), cell * 0.05, 0, 7); ctx.fill();
        ctx.fillStyle = "rgba(0,0,0,0.16)"; ctx.beginPath(); ctx.arc(x + cell * (0.65 - (h % 2) * 0.15), y + cell * (0.7 - (h % 3) * 0.12), cell * 0.045, 0, 7); ctx.fill();
        if (g.em[r][c]) { drawEmerald(ctx, x + cell / 2, y + cell / 2, cell * 0.32); }
      }
    }
    // cherry
    if (g.hasCherry) drawCherry(ctx, ox + g.cherry.c * cell + cell / 2, oy + g.cherry.r * cell + cell / 2, cell * 0.32);
    // bags
    for (const b of g.bags) { const x = ox + b.c * cell + cell / 2, y = oy + b.r * cell + cell / 2 + (b.state === "fall" ? b.y : 0) + (b.state === "wobble" ? Math.sin(g.t * 40) * 2 : 0); drawBag(ctx, x, y, cell * 0.34, b.state === "gold"); }
    // enemies
    for (const e of g.enemies) { if (!e.alive || g.t < e.spawnT) continue; drawEnemy(ctx, e.x, e.y, cell * 0.34, g.scared > 0); }
    // player
    drawDigger(ctx, g.player.x, g.player.y, cell * 0.42, g.player.dir, colorRef.current, g.dead);
    // drijvende score-popups
    if (g.pops) for (const p of g.pops) {
      ctx.save();
      ctx.globalAlpha = Math.max(0, 1 - p.t);
      ctx.font = `bold ${cell * 0.6}px system-ui, sans-serif`;
      ctx.textAlign = "center"; ctx.textBaseline = "middle";
      ctx.lineWidth = Math.max(2, cell * 0.08); ctx.strokeStyle = "rgba(0,0,0,0.75)";
      ctx.fillStyle = "#ffe34a";
      const yy = p.y - p.t * cell * 1.6;
      ctx.strokeText(p.text, p.x, yy); ctx.fillText(p.text, p.x, yy);
      ctx.restore();
    }
  };

  const drawEmerald = (ctx, x, y, s) => {
    const g = ctx.createLinearGradient(x, y - s, x, y + s);
    g.addColorStop(0, "#b9ffc8"); g.addColorStop(0.45, "#22e05c"); g.addColorStop(1, "#0a8f3c");
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.moveTo(x, y - s); ctx.lineTo(x + s * 0.85, y - s * 0.25); ctx.lineTo(x + s * 0.55, y + s);
    ctx.lineTo(x - s * 0.55, y + s); ctx.lineTo(x - s * 0.85, y - s * 0.25); ctx.closePath(); ctx.fill();
    ctx.strokeStyle = "rgba(6,80,30,0.85)"; ctx.lineWidth = Math.max(1, s * 0.08); ctx.lineJoin = "round"; ctx.stroke();
    ctx.fillStyle = "rgba(255,255,255,0.5)"; ctx.beginPath();
    ctx.moveTo(x, y - s); ctx.lineTo(x + s * 0.85, y - s * 0.25); ctx.lineTo(x, y - s * 0.02); ctx.lineTo(x - s * 0.85, y - s * 0.25); ctx.closePath(); ctx.fill();
    ctx.strokeStyle = "rgba(255,255,255,0.55)"; ctx.lineWidth = Math.max(1, s * 0.05); ctx.beginPath();
    ctx.moveTo(x - s * 0.85, y - s * 0.25); ctx.lineTo(x, y - s * 0.02); ctx.lineTo(x + s * 0.85, y - s * 0.25); ctx.moveTo(x, y - s * 0.02); ctx.lineTo(x, y + s); ctx.stroke();
    ctx.fillStyle = "#fff"; ctx.beginPath(); ctx.arc(x - s * 0.28, y - s * 0.42, s * 0.1, 0, 7); ctx.fill();
  };
  const drawBag = (ctx, x, y, s, gold) => {
    ctx.save();
    ctx.shadowColor = "rgba(0,0,0,0.45)"; ctx.shadowBlur = s * 0.35; ctx.shadowOffsetY = s * 0.18;
    const g = ctx.createRadialGradient(x - s * 0.3, y - s * 0.2, s * 0.1, x, y + s * 0.2, s * 1.35);
    if (gold) { g.addColorStop(0, "#fff6c0"); g.addColorStop(0.5, "#f7c727"); g.addColorStop(1, "#a9780a"); }
    else { g.addColorStop(0, "#e8c98f"); g.addColorStop(0.5, "#c6a05c"); g.addColorStop(1, "#7a5a28"); }
    ctx.fillStyle = g;
    ctx.beginPath(); ctx.moveTo(x - s * 0.5, y - s * 0.5);
    ctx.quadraticCurveTo(x - s * 1.05, y + s * 0.3, x - s * 0.55, y + s * 0.95);
    ctx.quadraticCurveTo(x, y + s * 1.15, x + s * 0.55, y + s * 0.95);
    ctx.quadraticCurveTo(x + s * 1.05, y + s * 0.3, x + s * 0.5, y - s * 0.5);
    ctx.closePath(); ctx.fill();
    ctx.shadowBlur = 0; ctx.shadowOffsetY = 0;
    ctx.fillStyle = gold ? "#8a6508" : "#5a4018";
    ctx.beginPath(); ctx.roundRect(x - s * 0.52, y - s * 0.78, s * 1.04, s * 0.32, s * 0.12); ctx.fill();
    ctx.fillStyle = "rgba(255,255,255,0.35)"; ctx.beginPath(); ctx.ellipse(x - s * 0.3, y + s * 0.15, s * 0.2, s * 0.42, -0.3, 0, 7); ctx.fill();
    ctx.fillStyle = gold ? "#7a5500" : "#3a2a12"; ctx.font = `bold ${s * 1.0}px system-ui, sans-serif`; ctx.textAlign = "center"; ctx.textBaseline = "middle"; ctx.fillText("$", x, y + s * 0.28);
    ctx.restore();
  };
  const drawCherry = (ctx, x, y, s) => {
    ctx.save();
    ctx.strokeStyle = "#3a7d2a"; ctx.lineWidth = Math.max(2, s * 0.13); ctx.lineCap = "round";
    ctx.beginPath(); ctx.moveTo(x - s * 0.45, y + s * 0.35); ctx.quadraticCurveTo(x, y - s, x + s * 0.35, y - s * 0.95); ctx.stroke();
    ctx.fillStyle = "#4caf3a"; ctx.beginPath(); ctx.ellipse(x + s * 0.6, y - s * 0.92, s * 0.42, s * 0.18, -0.6, 0, 7); ctx.fill();
    for (const [cx, cy] of [[-s * 0.42, s * 0.45], [s * 0.42, s * 0.35]]) {
      ctx.shadowColor = "rgba(0,0,0,0.4)"; ctx.shadowBlur = s * 0.3; ctx.shadowOffsetY = s * 0.12;
      const g = ctx.createRadialGradient(x + cx - s * 0.18, y + cy - s * 0.18, s * 0.05, x + cx, y + cy, s * 0.6);
      g.addColorStop(0, "#ff7080"); g.addColorStop(0.6, "#e0203a"); g.addColorStop(1, "#9c0f22");
      ctx.fillStyle = g; ctx.beginPath(); ctx.arc(x + cx, y + cy, s * 0.5, 0, 7); ctx.fill();
      ctx.shadowBlur = 0; ctx.shadowOffsetY = 0;
      ctx.fillStyle = "rgba(255,255,255,0.7)"; ctx.beginPath(); ctx.arc(x + cx - s * 0.18, y + cy - s * 0.2, s * 0.12, 0, 7); ctx.fill();
    }
    ctx.restore();
  };
  const drawEnemy = (ctx, x, y, s, scared) => {
    ctx.save();
    ctx.shadowColor = "rgba(0,0,0,0.4)"; ctx.shadowBlur = s * 0.35; ctx.shadowOffsetY = s * 0.15;
    const g = ctx.createRadialGradient(x - s * 0.3, y - s * 0.4, s * 0.1, x, y, s * 1.35);
    if (scared) { g.addColorStop(0, "#bcd8ff"); g.addColorStop(0.6, "#4a90ff"); g.addColorStop(1, "#1f5fd0"); }
    else { g.addColorStop(0, "#dca0f2"); g.addColorStop(0.55, "#a23bd6"); g.addColorStop(1, "#6a1f96"); }
    ctx.fillStyle = g;
    ctx.beginPath(); ctx.arc(x, y - s * 0.1, s, Math.PI, 0); ctx.lineTo(x + s, y + s * 0.6);
    const waves = 4;
    for (let i = 0; i < waves; i++) { const wx = x + s - (i + 1) * (2 * s / waves); ctx.lineTo(wx + s / waves, y + s * 0.85); ctx.lineTo(wx, y + s * 0.55); }
    ctx.closePath(); ctx.fill();
    ctx.shadowBlur = 0; ctx.shadowOffsetY = 0;
    ctx.strokeStyle = scared ? "#1f5fd0" : "#6a1f96"; ctx.lineWidth = Math.max(1, s * 0.1); ctx.lineCap = "round";
    ctx.beginPath(); ctx.moveTo(x - s * 0.35, y - s * 0.9); ctx.lineTo(x - s * 0.5, y - s * 1.25); ctx.moveTo(x + s * 0.35, y - s * 0.9); ctx.lineTo(x + s * 0.5, y - s * 1.25); ctx.stroke();
    ctx.fillStyle = scared ? "#bcd8ff" : "#ffd84a"; ctx.beginPath(); ctx.arc(x - s * 0.5, y - s * 1.3, s * 0.13, 0, 7); ctx.arc(x + s * 0.5, y - s * 1.3, s * 0.13, 0, 7); ctx.fill();
    ctx.fillStyle = "#fff"; ctx.beginPath(); ctx.arc(x - s * 0.32, y - s * 0.12, s * 0.32, 0, 7); ctx.arc(x + s * 0.32, y - s * 0.12, s * 0.32, 0, 7); ctx.fill();
    ctx.fillStyle = "#111"; ctx.beginPath(); ctx.arc(x - s * 0.27, y - s * 0.08, s * 0.15, 0, 7); ctx.arc(x + s * 0.37, y - s * 0.08, s * 0.15, 0, 7); ctx.fill();
    ctx.fillStyle = "#fff"; ctx.beginPath(); ctx.arc(x - s * 0.33, y - s * 0.15, s * 0.06, 0, 7); ctx.arc(x + s * 0.31, y - s * 0.15, s * 0.06, 0, 7); ctx.fill();
    if (scared) { ctx.strokeStyle = "#fff"; ctx.lineWidth = Math.max(1, s * 0.08); ctx.beginPath(); ctx.arc(x, y + s * 0.45, s * 0.22, Math.PI, 0); ctx.stroke(); }
    else { ctx.fillStyle = "#3a0f5a"; ctx.beginPath(); ctx.ellipse(x, y + s * 0.42, s * 0.22, s * 0.14, 0, 0, 7); ctx.fill(); }
    ctx.restore();
  };
  const drawDigger = (ctx, x, y, s, dir, col, dead) => {
    ctx.save(); ctx.translate(x, y);
    if (dir === "left") ctx.scale(-1, 1);
    if (dir === "up") ctx.rotate(-Math.PI / 2); if (dir === "down") ctx.rotate(Math.PI / 2);
    const base = dead ? "#9aa0a6" : col;
    // tracks/base + schaduw
    ctx.shadowColor = "rgba(0,0,0,0.45)"; ctx.shadowBlur = s * 0.4; ctx.shadowOffsetY = s * 0.2;
    ctx.fillStyle = "#1c1c22"; ctx.beginPath(); ctx.roundRect(-s * 0.95, s * 0.15, s * 1.8, s * 0.55, s * 0.25); ctx.fill();
    ctx.shadowBlur = 0; ctx.shadowOffsetY = 0;
    ctx.fillStyle = "#3a3a44"; for (const wx of [-0.6, -0.15, 0.3, 0.7]) { ctx.beginPath(); ctx.arc(s * wx, s * 0.42, s * 0.18, 0, 7); ctx.fill(); }
    ctx.fillStyle = "#15151a"; for (const wx of [-0.6, -0.15, 0.3, 0.7]) { ctx.beginPath(); ctx.arc(s * wx, s * 0.42, s * 0.08, 0, 7); ctx.fill(); }
    // body + glans-gradient
    ctx.fillStyle = base; ctx.beginPath(); ctx.roundRect(-s * 0.85, -s * 0.6, s * 1.5, s * 0.85, s * 0.22); ctx.fill();
    const bg = ctx.createLinearGradient(0, -s * 0.6, 0, s * 0.25);
    bg.addColorStop(0, "rgba(255,255,255,0.5)"); bg.addColorStop(0.22, "rgba(255,255,255,0)"); bg.addColorStop(1, "rgba(0,0,0,0.35)");
    ctx.fillStyle = bg; ctx.beginPath(); ctx.roundRect(-s * 0.85, -s * 0.6, s * 1.5, s * 0.85, s * 0.22); ctx.fill();
    // cabineglas + reflectie
    const gg = ctx.createLinearGradient(-s * 0.2, -s * 0.5, s * 0.5, 0);
    gg.addColorStop(0, "#d3f0ff"); gg.addColorStop(1, "#5aa0d0");
    ctx.fillStyle = gg; ctx.beginPath(); ctx.roundRect(-s * 0.12, -s * 0.5, s * 0.6, s * 0.5, s * 0.12); ctx.fill();
    ctx.fillStyle = "rgba(255,255,255,0.6)"; ctx.beginPath(); ctx.moveTo(-s * 0.02, -s * 0.45); ctx.lineTo(s * 0.2, -s * 0.45); ctx.lineTo(-s * 0.02, -s * 0.02); ctx.closePath(); ctx.fill();
    // koplamp
    ctx.fillStyle = "#fff3b0"; ctx.beginPath(); ctx.arc(-s * 0.62, -s * 0.18, s * 0.1, 0, 7); ctx.fill();
    // draaiende boorkop vooraan
    ctx.save(); ctx.translate(s * 0.78, 0);
    const spin = (G.current ? G.current.t : 0) * 9;
    ctx.fillStyle = "#c9ccd2"; ctx.beginPath(); ctx.moveTo(0, -s * 0.5); ctx.lineTo(s * 0.55, 0); ctx.lineTo(0, s * 0.5); ctx.closePath(); ctx.fill();
    ctx.strokeStyle = "#6c7078"; ctx.lineWidth = Math.max(1, s * 0.06);
    for (let i = 0; i < 3; i++) { const o = ((spin + i * 0.5) % 1) * s * 0.45; ctx.beginPath(); ctx.moveTo(o, -s * 0.5 + o); ctx.lineTo(o, s * 0.5 - o); ctx.stroke(); }
    ctx.fillStyle = "#8a8e96"; ctx.beginPath(); ctx.roundRect(-s * 0.14, -s * 0.5, s * 0.16, s, s * 0.05); ctx.fill();
    ctx.restore();
    ctx.restore();
  };

  const openBoard = async () => { setPhase("board"); try { const { data } = await api.get("/child/digger-leaderboard"); setBoard(data); } catch { } };
  const muted = { color: "var(--t-muted)" };

  // ---------- MENU ----------
  if (phase === "menu")
    return (
      <ThemedShell>
        <div className="max-w-md mx-auto px-5 pt-8 pb-16">
          <div className="flex items-center gap-2 mb-6">
            <Button variant="ghost" onClick={() => navigate("/leerling/speelhoek")} className="rounded-full px-3" style={{ color: "var(--t-text)" }} data-testid="digger-back"><ArrowLeft className="w-5 h-5" /></Button>
            <h1 className="font-heading text-2xl font-black themed-heading">Digger 🚜</h1>
          </div>
          <p className="font-semibold mb-5" style={muted}>Graaf tunnels, verzamel alle smaragden 💎 en ontwijk de aliens 👾! Laat goudzakken op ze vallen. Pak de kers 🍒 om ze even op te eten. Haal alle {MAX_LEVEL} levels!</p>

          <ThemedCard className="p-5 mb-4">
            <p className="font-heading font-extrabold mb-3 themed-heading">Kies je kleur</p>
            <div className="grid grid-cols-6 gap-2">
              {COLORS.map((cc) => (
                <button key={cc.c} onClick={() => setColor(cc.c)} data-testid={`digger-color-${cc.name}`} title={cc.name}
                  className="aspect-square rounded-full transition active:scale-90" style={{ background: cc.c, outline: color === cc.c ? "3px solid var(--t-text)" : "none", outlineOffset: 2 }} />
              ))}
            </div>
            <label className="flex items-center gap-2 mt-4 font-semibold text-sm" style={muted}>
              <input type="checkbox" checked={showPad} onChange={(e) => setShowPad(e.target.checked)} data-testid="digger-pad-toggle" /> Pijltjes-knoppen tonen (swipen kan altijd)
            </label>
          </ThemedCard>

          <Button onClick={startGame} data-testid="digger-start-btn" className="w-full py-6 rounded-2xl font-bold text-lg border-0 mb-3" style={{ background: "var(--t-primary)", color: "var(--t-on-primary)" }}><Gamepad2 className="w-5 h-5 mr-2" /> Start spel</Button>
          <Button onClick={openBoard} data-testid="digger-board-btn" className="w-full h-12 rounded-xl font-bold border-2 bg-transparent" style={{ borderColor: "var(--t-border)", color: "var(--t-text)" }}><Trophy className="w-4 h-4 mr-1.5" style={{ color: "var(--t-accent)" }} /> Topscores</Button>
          <button onClick={() => setShowHelp(true)} data-testid="digger-help-btn" className="w-full mt-3 flex items-center justify-center gap-1.5 text-sm font-bold" style={{ color: "var(--t-muted)" }}><HelpCircle className="w-4 h-4" /> Hoe speel je?</button>
        </div>

        <AnimatePresence>
          {showHelp && (
            <motion.div key="help" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={closeHelp} className="fixed inset-0 z-30 flex items-center justify-center p-5" style={{ background: "rgba(0,0,0,0.6)" }} data-testid="digger-help-modal">
              <motion.div onClick={(e) => e.stopPropagation()} initial={{ scale: 0.8, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.85, opacity: 0 }} transition={{ type: "spring", stiffness: 280, damping: 20 }}
                className="w-full max-w-sm rounded-[1.8rem] p-6" style={{ background: "var(--t-surface)", border: "3px solid var(--t-accent)" }}>
                <h2 className="font-heading text-2xl font-black themed-heading text-center mb-4">Zo speel je Digger 🚜</h2>
                <div className="space-y-3">
                  {[
                    ["🕹️", "Beweeg met de pijltjes of swipe — je graaft tunnels door de aarde."],
                    ["⏹️", "Tik op het scherm (of de stop-knop) om stil te blijven staan."],
                    ["💎", "Verzamel álle smaragden om het level te halen."],
                    ["👾", "Pas op de aliens! Raak ze niet aan."],
                    ["🍒", "Pak de kers: aliens worden bang (blauw) — ren ze omver om ze op te eten (+200)!"],
                    ["💰", "Duw goudzakken opzij en laat ze op een alien vallen (+200)."],
                    ["❤️", `Je hebt 2 levens. Haal alle ${MAX_LEVEL} levels!`],
                  ].map(([emo, txt], i) => (
                    <div key={i} className="flex items-start gap-3">
                      <span className="text-2xl shrink-0 leading-none">{emo}</span>
                      <p className="text-sm font-semibold" style={{ color: "var(--t-text)" }}>{txt}</p>
                    </div>
                  ))}
                </div>
                <Button onClick={closeHelp} data-testid="digger-help-close" className="w-full mt-6 h-12 rounded-xl font-bold border-0" style={{ background: "var(--t-primary)", color: "var(--t-on-primary)" }}>Aan de slag! 🚜</Button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </ThemedShell>
    );

  // ---------- BOARD ----------
  if (phase === "board")
    return (
      <ThemedShell>
        <div className="max-w-md mx-auto px-5 pt-8 pb-16">
          <div className="flex items-center gap-2 mb-6">
            <Button variant="ghost" onClick={() => setPhase("menu")} className="rounded-full px-3" style={{ color: "var(--t-text)" }} data-testid="digger-board-back"><ArrowLeft className="w-5 h-5" /></Button>
            <h1 className="font-heading text-2xl font-black themed-heading">Digger Topscores 🏆</h1>
          </div>
          <ThemedCard className="p-4" data-testid="digger-board">
            {!board ? <p className="text-center py-6" style={muted}>Laden…</p> : board.top.length === 0 ? <p className="text-center py-6" style={muted}>Nog geen scores. Wees de eerste!</p> :
              board.top.map((r, i) => (
                <div key={i} className="flex items-center gap-3 px-2 py-2.5 rounded-xl" style={{ background: r.me ? "var(--t-surface2)" : "transparent" }}>
                  <span className="w-7 font-heading font-black text-lg" style={{ color: i === 0 ? "#f4c40f" : i === 1 ? "#b8c0cc" : i === 2 ? "#d08b50" : "var(--t-muted)" }}>{i + 1}</span>
                  <Medal className="w-4 h-4" style={{ color: i < 3 ? "var(--t-accent)" : "var(--t-muted)" }} />
                  <span className="flex-1 font-bold">{r.name}{r.me ? " (jij)" : ""} <span className="text-xs" style={muted}>· lvl {r.level}</span></span>
                  <span className="font-heading font-black">{r.best}</span>
                </div>
              ))}
          </ThemedCard>
        </div>
      </ThemedShell>
    );

  // ---------- OVER / WIN ----------
  if (phase === "over" || phase === "win")
    return (
      <ThemedShell>
        <div className="max-w-md mx-auto px-5 pt-14 pb-16 text-center">
          <div className="text-6xl mb-2">{phase === "win" ? "🏆" : "🚜💥"}</div>
          <h1 className="font-heading text-3xl font-black themed-heading">{phase === "win" ? "Alle levels gehaald!" : "Game over"}</h1>
          <p className="font-heading text-4xl font-black mt-3 themed-heading" data-testid="digger-final-score">{result?.score ?? scoreRef.current}</p>
          <p className="font-semibold mt-1" style={muted}>Level {result?.level ?? 1}</p>
          {result && result.xp_earned != null && (
            <div className="flex justify-center gap-4 mt-4">
              <span className="font-heading font-extrabold" style={{ color: "var(--t-accent)" }}>+{result.xp_earned} XP</span>
              <span className="font-heading font-extrabold" style={{ color: "var(--t-primary)" }}>+{result.coins_earned} 🪙</span>
            </div>
          )}
          <div className="flex flex-col gap-2 mt-7">
            <Button onClick={startGame} data-testid="digger-again-btn" className="h-12 rounded-xl font-bold border-0" style={{ background: "var(--t-primary)", color: "var(--t-on-primary)" }}><RotateCcw className="w-4 h-4 mr-1.5" /> Opnieuw vanaf level 1</Button>
            <Button onClick={openBoard} className="h-12 rounded-xl font-bold border-2 bg-transparent" style={{ borderColor: "var(--t-border)", color: "var(--t-text)" }}><Trophy className="w-4 h-4 mr-1.5" style={{ color: "var(--t-accent)" }} /> Topscores</Button>
            <Button onClick={() => navigate("/leerling/speelhoek")} className="h-12 rounded-xl font-bold border-2 bg-transparent" style={{ borderColor: "var(--t-border)", color: "var(--t-text)" }}><Home className="w-4 h-4 mr-1.5" /> Speelhoek</Button>
          </div>
        </div>
      </ThemedShell>
    );

  // ---------- PLAY ----------
  return (
    <div className="fixed inset-0 overflow-hidden select-none" style={{ background: "#0a0a0a" }} data-testid="digger-stage">
      <div className="absolute top-0 inset-x-0 z-10 flex items-center justify-between px-4 py-2 text-white font-heading font-black" style={{ background: "rgba(0,0,0,0.5)" }}>
        <button onClick={() => { cancelAnimationFrame(rafRef.current); setPhase("menu"); }} data-testid="digger-stage-back"><ArrowLeft className="w-6 h-6" /></button>
        <span data-testid="digger-hud-level">Level {hud.level}</span>
        <span>💎 {hud.emer}</span>
        <span data-testid="digger-hud-lives">{"❤️".repeat(Math.max(0, hud.lives + 1))}</span>
        <span data-testid="digger-hud-score">{hud.score}</span>
      </div>
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full touch-none" onPointerDown={onDown} onPointerUp={onUp} data-testid="digger-canvas" />
      <AnimatePresence>
        {banner && (
          <motion.div key="lvlbanner" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="absolute inset-0 z-20 flex items-center justify-center" style={{ background: "rgba(0,0,0,0.6)" }} data-testid="digger-level-banner">
            <motion.div initial={{ scale: 0.55, y: 24 }} animate={{ scale: 1, y: 0 }} transition={{ type: "spring", stiffness: 280, damping: 16 }}
              className="text-center px-9 py-8 rounded-[2rem]" style={{ background: "#1c1206", border: "3px solid #f4c40f", boxShadow: "0 18px 50px rgba(0,0,0,0.5)" }}>
              <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.15, type: "spring", stiffness: 300 }} className="text-6xl mb-1">🎉</motion.div>
              <h2 className="font-heading text-3xl font-black" style={{ color: "#fff" }}>Level {banner.level} gehaald!</h2>
              <p className="font-heading text-xl font-extrabold mt-3" style={{ color: "#2bdc4e" }}>+{banner.bonus} punten bonus 💎</p>
              <p className="font-heading text-5xl font-black mt-1" style={{ color: "#f4c40f" }}>{banner.score}</p>
              <p className="font-semibold mt-3 text-sm" style={{ color: "rgba(255,255,255,0.65)" }}>Door naar level {banner.level + 1}…</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      {showPad && (
        <div className="absolute bottom-5 inset-x-0 flex justify-center z-10 pointer-events-none">
          <div className="grid grid-cols-3 gap-3 w-64 sm:w-72" style={{ pointerEvents: "auto" }}>
            <span />
            <button onPointerDown={() => setDir("up")} data-testid="dpad-up" className="aspect-square rounded-2xl flex items-center justify-center text-white/90 active:scale-95 transition-transform" style={{ background: "rgba(255,255,255,0.12)", backdropFilter: "blur(2px)" }}><ArrowUp className="w-9 h-9" strokeWidth={2.5} /></button>
            <span />
            <button onPointerDown={() => setDir("left")} data-testid="dpad-left" className="aspect-square rounded-2xl flex items-center justify-center text-white/90 active:scale-95 transition-transform" style={{ background: "rgba(255,255,255,0.12)", backdropFilter: "blur(2px)" }}><ArrowL className="w-9 h-9" strokeWidth={2.5} /></button>
            <button onPointerDown={stopDigger} data-testid="dpad-stop" className="aspect-square rounded-2xl flex items-center justify-center text-white/80 active:scale-95 transition-transform" style={{ background: "rgba(255,255,255,0.08)", backdropFilter: "blur(2px)" }}><Square className="w-6 h-6" fill="currentColor" /></button>
            <button onPointerDown={() => setDir("right")} data-testid="dpad-right" className="aspect-square rounded-2xl flex items-center justify-center text-white/90 active:scale-95 transition-transform" style={{ background: "rgba(255,255,255,0.12)", backdropFilter: "blur(2px)" }}><ArrowRight className="w-9 h-9" strokeWidth={2.5} /></button>
            <span />
            <button onPointerDown={() => setDir("down")} data-testid="dpad-down" className="aspect-square rounded-2xl flex items-center justify-center text-white/90 active:scale-95 transition-transform" style={{ background: "rgba(255,255,255,0.12)", backdropFilter: "blur(2px)" }}><ArrowDown className="w-9 h-9" strokeWidth={2.5} /></button>
            <span />
          </div>
        </div>
      )}
      <button onClick={() => setShowPad((s) => !s)} data-testid="digger-pad-btn" className="absolute top-16 right-4 z-10 w-11 h-11 rounded-full flex items-center justify-center text-white/80" style={{ background: "rgba(255,255,255,0.12)" }}><Gamepad2 className="w-5 h-5" /></button>
    </div>
  );
}
